import * as PIXI from 'pixi.js';
import type { ICollisionObject } from '@/types/CollisionTypes';

export interface PolygonColliderOptions {
  points: number[]; // Array of x,y coordinates [x1, y1, x2, y2, ...]
  color?: number;
  visible?: boolean;
}

export class PolygonCollider implements ICollisionObject {
  public sprite: PIXI.Graphics;
  public points: number[];
  private polygon: PIXI.Polygon;
  private color: number;
  private isVisible: boolean;

  constructor(options: PolygonColliderOptions) {
    const { points, color = 0xff0000, visible = false } = options;

    if (points.length < 6) {
      throw new Error('Polygon must have at least 3 vertices (6 coordinates)');
    }

    this.points = [...points];
    this.polygon = new PIXI.Polygon(points);
    this.color = color;
    this.isVisible = visible;

    // Create visual representation of the collider
    this.sprite = new PIXI.Graphics();
    
    // Draw initial state
    this.redraw();
  }

  private redraw(): void {
    this.sprite.clear();
    
    if (this.isVisible) {
      this.sprite.beginFill(this.color, 0.3); // Semi-transparent
      this.sprite.lineStyle(2, this.color, 0.8);
      this.sprite.drawPolygon(this.points);
      this.sprite.endFill();
    }
  }

  /**
   * Check if a point is inside this polygon collider
   */
  public containsPoint(x: number, y: number): boolean {
    return this.polygon.contains(x, y);
  }

  /**
   * Check if a rectangle intersects with this polygon collider
   */
  public intersects(rect: PIXI.Rectangle): boolean {
    // Check if any corner of the rectangle is inside the polygon
    const corners = [
      { x: rect.x, y: rect.y },
      { x: rect.x + rect.width, y: rect.y },
      { x: rect.x, y: rect.y + rect.height },
      { x: rect.x + rect.width, y: rect.y + rect.height },
    ];

    for (const corner of corners) {
      if (this.polygon.contains(corner.x, corner.y)) {
        return true;
      }
    }

    // Check if any edge of the polygon intersects with the rectangle
    for (let i = 0; i < this.points.length; i += 2) {
      const x = this.points[i];
      const y = this.points[i + 1];
      if (rect.contains(x, y)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if another polygon collider intersects with this one
   */
  public intersectsPolygonCollider(other: PolygonCollider): boolean {
    // Check if any vertex of the other polygon is inside this one
    for (let i = 0; i < other.points.length; i += 2) {
      const x = other.points[i];
      const y = other.points[i + 1];
      if (this.polygon.contains(x, y)) {
        return true;
      }
    }

    // Check if any vertex of this polygon is inside the other one
    for (let i = 0; i < this.points.length; i += 2) {
      const x = this.points[i];
      const y = this.points[i + 1];
      if (other.polygon.contains(x, y)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Get the bounding box of this polygon
   */
  public getBounds(): PIXI.Rectangle {
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    for (let i = 0; i < this.points.length; i += 2) {
      const x = this.points[i];
      const y = this.points[i + 1];
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }

    return new PIXI.Rectangle(minX, minY, maxX - minX, maxY - minY);
  }

  /**
   * Set visibility of the collider
   */
  public setVisible(visible: boolean): void {
    if (this.isVisible !== visible) {
      this.isVisible = visible;
      this.redraw();
    }
  }

  /**
   * Export polygon data in a format suitable for configuration
   */
  public exportData(): { points: number[]; color: number; visible: boolean } {
    return {
      points: [...this.points],
      color: this.color,
      visible: this.isVisible,
    };
  }
}
